import { ArrowRight, Bank, CoinsHand, HelpCircle } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import type { IntakeInsuranceMode } from "@/lib/api/public-consumer"
import { AInput, ASelect, SelectTile } from "@/components/onboarding-activation/ui"
import { FlowCta, FlowHeading, IntakeField, PhasePane, QuietLink } from "./flow-chrome"
import { CONSUMER_V2, INSURANCE_CARRIERS } from "./copy"

// Insurance vs out-of-pocket — three tiles; picking "Through insurance"
// reveals carrier + claim number. Skippable, like every extra.

const C = CONSUMER_V2.extras.insurance

const MODES: { value: IntakeInsuranceMode; label: string; icon: React.ReactNode }[] = [
  { value: "insurance", label: "Through insurance", icon: <Bank className="size-4" aria-hidden /> },
  { value: "out_of_pocket", label: "Paying myself", icon: <CoinsHand className="size-4" aria-hidden /> },
  { value: "unsure", label: "Not sure yet", icon: <HelpCircle className="size-4" aria-hidden /> },
]

export function StepInsurance({
  insurance,
  setInsurance,
  onDone,
  onSkip,
}: {
  insurance: { mode: IntakeInsuranceMode | null; carrier: string; claimNumber: string }
  setInsurance: (
    patch: Partial<{ mode: IntakeInsuranceMode | null; carrier: string; claimNumber: string }>,
  ) => void
  onDone: () => void
  onSkip: () => void
}) {
  return (
    <PhasePane>
      <header className="mt-5">
        <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
          {C.title}
        </FlowHeading>
        <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{C.sub}</p>
      </header>

      <div className="mt-5 flex-1 lg:pointer-fine:flex-none">
        <div className="space-y-2.5" role="radiogroup" aria-label={C.title}>
          {MODES.map((m) => (
            <SelectTile
              key={m.value}
              selected={insurance.mode === m.value}
              onClick={() =>
                setInsurance({ mode: insurance.mode === m.value ? null : m.value })
              }
            >
              <span className="inline-flex items-center gap-2">
                {m.icon}
                {m.label}
              </span>
            </SelectTile>
          ))}
        </div>

        <AnimatePresence initial={false}>
          {insurance.mode === "insurance" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className="overflow-hidden"
            >
              <div className="mt-5 space-y-4">
                <IntakeField label={C.carrierLabel}>
                  {(control) => (
                    <div role="group" aria-labelledby={`${control.id}-label`}>
                      <ASelect
                        value={insurance.carrier}
                        onChange={(carrier) => setInsurance({ carrier })}
                        options={[...INSURANCE_CARRIERS]}
                        placeholder={C.carrierPlaceholder}
                      />
                    </div>
                  )}
                </IntakeField>
                <IntakeField label={C.claimLabel}>
                  {(control) => (
                    <AInput
                      {...control}
                      value={insurance.claimNumber}
                      onChange={(e) => setInsurance({ claimNumber: e.target.value })}
                      placeholder={C.claimPlaceholder}
                      maxLength={40}
                      autoCapitalize="characters"
                      autoComplete="off"
                      className="h-13 rounded-xl text-[17px]"
                    />
                  )}
                </IntakeField>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-6 space-y-2">
        <FlowCta onClick={onDone}>
          {CONSUMER_V2.extras.continue}
          <ArrowRight className="size-4" aria-hidden />
        </FlowCta>
        <QuietLink onClick={onSkip}>{CONSUMER_V2.extras.skip}</QuietLink>
      </div>
    </PhasePane>
  )
}
